import { useCallback, useContext, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentButton from '../../../draft-content-transplant/ContentButton';
import { CLICK_MANAGE_PAYMENTS, INSERT_PAYMENT } from 'rich-text-lib/constants/usageTracking';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { TextToolbarIcon } from 'rich-text-lib/components/editor/TextToolbarIcon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import PaymentPopoverEmptyState from './PaymentPopoverEmptyState';
import PaymentPopoverErrorState from './PaymentPopoverErrorState';
import transformPaymentLinksData from './transformPaymentLinksData';
import * as PaymentLinksApi from './PaymentLinksApi';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import InsertContentDecorator from '../../../draft-transplant/InsertContentDecoratorV2';
import hubHttpClient from 'hub-http/clients/apiClient';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import GenericPopoverHeader from '../../components/GenericPopoverHeader';
import PortalIdParser from 'PortalIdParser';
import identity from 'transmute/identity';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import { insertLink } from '../../utils/documentUtils';
import ContentContainer from '../../components/insertMenu/ContentContainer';
import { getFullUrl } from 'hubspot-url-utils';
import WrappedPaymentLinkItem from './WrappedPaymentLinkItem';
import { Tracker } from '../../../utils/usageTracker';
import { PM_PAYMENT } from '../../constants/events';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { LinkPlugin } from '../../plugins/link/LinkPlugin';
import { getGlobalVars } from '../../utils/globalVarUtil';
import { CapIcon } from 'foundations-assets/icons/components/CapIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const PaymentLinkButton = ({
  onClick
}) => /*#__PURE__*/_jsx(Tooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertPaymentLink"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    "data-test-id": "payment-links",
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(TextToolbarIcon, {
      icon: CapIcon
    })
  })
});
export const getPaymentLinkUrl = portalId => {
  return `${getFullUrl('app')}/payment-links/${portalId}`;
};

/** Configuration for the Payment Link insert capability */

const trackManage = () => Tracker.track(PM_PAYMENT, {
  action: CLICK_MANAGE_PAYMENTS
});
const trackInsert = () => Tracker.track(PM_PAYMENT, {
  action: INSERT_PAYMENT
});
export default function InsertPaymentLink({
  outputType = contentOutputs.RENDERED,
  useContentContainer,
  onViewInsertMenu = () => {},
  onInsertComplete = () => {},
  referrerParam
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    usePlaintextMode,
    plaintextOptions
  } = getGlobalVars(useContext(EditorConfigContext));
  const insertPaymentLink = useCallback(({
    htmlBody
  }) => {
    const tr = insertLink({
      htmlBody,
      editorState,
      to: editorState.selection.to,
      from: editorState.selection.from
    }).setMeta(LinkPlugin, {
      paymentLinkReferrer: referrerParam
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(editorState.apply(tr), plaintextOptions) : editorState.apply(tr));
    onInsertComplete();
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete, referrerParam]);
  const PaymentLinkPopover = useMemo(() => {
    const apiFunctions = PaymentLinksApi.defaultApiWrapper(hubHttpClient, referrerParam);
    const insertPaymentLinkDecoratorConfig = {
      outputType,
      apiFunctions,
      mergeTagOptions: {
        includeCustomTokens: true
      }
    };
    if (useContentContainer) {
      return InsertContentDecorator(ContentContainer({
        onClickManageButton: trackManage,
        onInsertContent: trackInsert,
        portalId: PortalIdParser.get(),
        PopoverErrorState: PaymentPopoverErrorState,
        PopoverEmptyState: PaymentPopoverEmptyState,
        Header: GenericPopoverHeader,
        searchPlaceHolder: 'hubspot-prosemirror.paymentLinks.popover.search.placeholder',
        transformData: transformPaymentLinksData,
        search: apiFunctions.searchLinks,
        onViewInsertMenu,
        // @ts-expect-error ts-migrate FIXME
        manageUrl: getPaymentLinkUrl(PortalIdParser.get()),
        titleMessage: 'hubspot-prosemirror.paymentLinks.popover.header.insertPaymentLink',
        CustomItemComponent: WrappedPaymentLinkItem,
        contentKey: 'paymentLinks'
      }), insertPaymentLinkDecoratorConfig);
    }
    return InsertContentDecorator(ContentButton({
      onInsertContent: trackInsert,
      // @ts-expect-error ts-migrate FIXME
      portalId: PortalIdParser.get(),
      Button: PaymentLinkButton,
      PopoverErrorState: PaymentPopoverErrorState,
      PopoverEmptyState: PaymentPopoverEmptyState,
      onOpenPopover: identity,
      Header: GenericPopoverHeader,
      searchPlaceHolder: 'hubspot-prosemirror.paymentLinks.popover.search.placeholder',
      transformData: transformPaymentLinksData,
      search: apiFunctions.searchLinks,
      popoverTestKey: 'payment-links-popover',
      // @ts-expect-error ts-migrate FIXME
      manageUrl: getPaymentLinkUrl(PortalIdParser.get()),
      titleMessage: 'hubspot-prosemirror.paymentLinks.popover.header.insertPaymentLink'
    }), insertPaymentLinkDecoratorConfig);
  }, [outputType, onViewInsertMenu, useContentContainer, referrerParam]);
  return /*#__PURE__*/_jsx(PaymentLinkPopover, {
    onChange: insertPaymentLink,
    useProsemirror: true
  });
}